/**
 * The app's single body-scroll lock.
 *
 * ─────────────────────────────────────────────────────────────────
 * WHY IT IS SHARED. Eleven components used to write
 * `document.body.style.overflow` themselves, and six of those ALSO rendered a
 * `BdrModal` — which has its own lock. So they double-locked: whichever ran
 * first hid the scrollbar, and whichever released first handed scrolling back
 * while the other modal was still open. The count is what makes stacking work;
 * it only means anything if everyone counts against the same number.
 *
 * ─────────────────────────────────────────────────────────────────
 * IT PAYS BACK THE WIDTH IT TAKES. `overflow: hidden` removes the document
 * scrollbar, so the viewport is suddenly ~20px wider and every centred thing
 * on the page — including the modal that just opened — jumps right, then jumps
 * back on close. Replacing the bar with padding of exactly its width means the
 * layout never moves.
 *
 * MEASURED, not assumed: the page may not be scrolling at all (nothing to
 * compensate), and an overlay-scrollbar platform — macOS with "show scroll
 * bars when scrolling", every touch device — also reports 0. Hard-coding 20
 * would push the page sideways on exactly the machines that never had the
 * problem. The width is captured at the 0 -> 1 transition and REMEMBERED,
 * because by the time the last modal releases the bar is already gone and
 * measuring again would read 0.
 *
 * ─────────────────────────────────────────────────────────────────
 * CALL IT IN PAIRS, FROM AN EFFECT'S CLEANUP:
 *
 *     useEffect(() => {
 *       if (!open) return undefined;
 *       lockBodyScroll();
 *       return () => releaseBodyScroll();
 *     }, [open]);
 *
 * The shape matters. The old `if (open) hidden; if (!open) auto;` form fires a
 * release on every render where the thing is shut — including the very first,
 * before it was ever open — so a count would drift below zero and a sibling
 * modal would lose its lock. An effect keyed on `open` with the release in the
 * cleanup is balanced by construction: React runs the cleanup exactly once per
 * run of the effect.
 */

let lockCount = 0;
let compensatedWidth = 0;

/* Subscribers are notified when the lock crosses 0 in either direction.
   `DocumentScrollbar` uses it to take its overlay down while a modal is open:
   the page cannot scroll then, but `scrollHeight` has not changed, so without
   this the thumb would keep rendering over the modal's backdrop. */
const listeners = new Set();

const notify = () => {
  const locked = lockCount > 0;
  listeners.forEach((fn) => fn(locked));
};

export const subscribeScrollLock = (fn) => {
  listeners.add(fn);
  fn(lockCount > 0);
  return () => listeners.delete(fn);
};

const scrollbarWidth = () =>
  window.innerWidth - document.documentElement.clientWidth;

export const lockBodyScroll = () => {
  lockCount += 1;
  if (lockCount !== 1) return;

  /* Since the document's native bar is hidden (DocumentScrollbar draws an
     overlay instead), this now measures 0 and the compensation self-disables.
     It is kept because it is still correct for any future state where the
     page does carry a native bar. */
  compensatedWidth = scrollbarWidth();
  document.body.style.overflow = "hidden";
  if (compensatedWidth > 0) {
    document.body.style.paddingRight = `${compensatedWidth}px`;
  }
  notify();
};

export const releaseBodyScroll = () => {
  /* Clamped rather than trusted: an unbalanced release from a component that
     has not been converted yet should not drive the count negative and leave
     the next modal unable to lock. */
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount !== 0) return;

  document.body.style.overflow = "auto";
  document.body.style.paddingRight = "";
  compensatedWidth = 0;
  notify();
};

/** Test/debug only — never branch on this in UI code. */
export const _lockCount = () => lockCount;
